// Colour + symbol + word for every state (SPEC 7.5). Never colour alone.
import type { Agent, AgentStatus, Card } from "@whistle/protocol";
import { promptState, type SendRecord } from "../lib/model";
import { IconActivity, IconCheck, IconDash, IconDesk, IconPause, IconQuestion, IconRecent, IconSend } from "./Icons";

/**
 * Amber ("waiting") is only for a card this phone can answer now. An approval or question it can't is "moved" (handed
 * back to the desk), "sent" (this phone's answer is on its way) or "pending" (not open for an answer at the moment).
 */
export type VisualState = "waiting" | "working" | "finished" | "idle" | "unknown" | "desk" | "maybe" | "moved" | "sent" | "pending";

export const STATE_WORD: Record<VisualState, string> = {
  waiting: "Needs answer",
  working: "Working",
  finished: "Finished",
  idle: "Idle",
  unknown: "Unknown",
  desk: "Needs your desk",
  maybe: "May be waiting",
  moved: "At your desk",
  sent: "Answer sent",
  pending: "Checking",
};

export function stateIcon(state: VisualState, props: { className?: string } = {}) {
  switch (state) {
    case "waiting":
      return <IconPause {...props} />;
    case "working":
      return <IconActivity {...props} />;
    case "finished":
      return <IconCheck {...props} />;
    case "idle":
      return <IconDash {...props} />;
    case "unknown":
      return <IconQuestion {...props} />;
    case "desk":
    case "moved":
      return <IconDesk {...props} />;
    case "maybe":
      return <IconQuestion {...props} />;
    case "sent":
      return <IconSend {...props} />;
    case "pending":
      return <IconRecent {...props} />;
  }
}

export function agentVisual(status: AgentStatus): VisualState {
  switch (status) {
    case "blocked":
      return "waiting";
    case "working":
      return "working";
    case "done":
      return "finished";
    case "idle":
      return "idle";
    default:
      return "unknown";
  }
}

/**
 * An agent in the strip. A blocked one is amber only while one of its cards can be answered from here; otherwise it
 * takes the look of the card that explains it, and with no card at all it is waiting at the desk.
 */
export function agentStripVisual(agent: Agent, cards: Card[], sends: Record<string, SendRecord> = {}): VisualState {
  if (agent.status !== "blocked") return agentVisual(agent.status);
  const own = cards.filter((c) => c.agentId === agent.id).map((c) => cardVisual(c, sends));
  const order: VisualState[] = ["waiting", "sent", "desk", "maybe", "moved", "pending"];
  return order.find((v) => own.includes(v)) ?? "moved";
}

export function cardVisual(card: Card, sends: Record<string, SendRecord> = {}): VisualState {
  switch (card.kind) {
    case "approval":
    case "question": {
      const p = promptState(card, sends);
      return p === "answer" ? "waiting" : p === "desk" ? "moved" : p === "sent" ? "sent" : "pending";
    }
    case "desk":
      return "desk";
    case "maybe":
      return "maybe";
    case "finished":
      return "finished";
  }
}

const CHIP_CLASS: Record<VisualState, string> = {
  waiting: "chip-waiting",
  working: "chip-working",
  finished: "chip-finished",
  idle: "chip-idle",
  unknown: "chip-unknown",
  desk: "chip-desk",
  maybe: "chip-unknown",
  moved: "chip-moved",
  sent: "chip-sent",
  pending: "chip-unknown",
};

export function StateChip({ state, word }: { state: VisualState; word?: string }) {
  return (
    <span className={`chip ${CHIP_CLASS[state]}`}>
      {stateIcon(state)}
      {word ?? STATE_WORD[state]}
    </span>
  );
}
